<template>
  <communication-log-call-dialog
    v-if="callDialog && type === PipType.Call"
    ref="communicationLogCallDialogRef"
    :value="callDialog"
    @close-dialog="closePiPModal"
  />
  <div v-else-if="type === PipType.DealActivity" ref="communicationLogNoteRef">
    <deal-activity-content />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { DealActivityState } from "@/enums/dealActivity";
import { PipType } from "@/enums/pip";
import {
  DEFAULT_CALL_MODAL_OPTIONS,
  DEFAULT_DEAL_ACTIVITY_OPTIONS,
} from "@/helpers/constants/deals";
import { useEmitter } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { usePiPView } from "@/hooks/pip";
import apiService from "@/services/modules/workflows";
import useCallsStore from "@/stores/calls";
import useNotesStore from "@/stores/notes";
import usePiPStore from "@/stores/pip";

import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import DealActivityContent from "@/components/deals/dealActivity/DealActivityContent.vue";

const notesStore = useNotesStore();
const callsStore = useCallsStore();
const { callDialog } = storeToRefs(callsStore);
const {
  isDealActivityPipModeActivated,
  activeNoteBusinessDealId,
  activeNoteDealId,
} = storeToRefs(notesStore);
const { activePiPDeal, activePiPFlows, activePiPStages, activePiPWorkflow } =
  storeToRefs(usePiPStore());
const { initPiPView } = usePiPView();
const { showMessage } = useNotification();
const emitter = useEmitter();
const { t } = useI18n();
const { activeDeal } = useDealsBase();

const closeDealActivityState = ref<DealActivityState>(DealActivityState.Closed);
const shouldUpdatePiPDeal = ref(false);

const { type } = defineProps<{
  type: PipType;
}>();

const emit = defineEmits<{
  "pip:hide": [];
}>();

const callOptions = useLocalStorageSetting(
  "callModalOptions",
  DEFAULT_CALL_MODAL_OPTIONS
);

const noteOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);

const unsetActivePiP = () => {
  isDealActivityPipModeActivated.value = false;
  activePiPDeal.value = null;
  activePiPWorkflow.value = null;
  activePiPFlows.value = [];
  activePiPStages.value = [];
};

emitter.on("CLOSE_PIP_VIEW", async () => {
  if (type === PipType.DealActivity) {
    closeDealActivityState.value = DealActivityState.Draggable;
    unsetActivePiP();
  }
  window.documentPictureInPicture?.window.close();
  emit("pip:hide");
});

emitter.on(
  "UPDATE_PIP_DEAL",
  async ({ setCurrentDeal }: { setCurrentDeal: boolean }) => {
    if (setCurrentDeal) {
      if (
        !activeDeal.value ||
        activePiPDeal.value?.id === activeDeal.value.id
      ) {
        return;
      }
      activePiPDeal.value = activeDeal.value;
      activeNoteDealId.value = activePiPDeal.value.id;
      await notesStore.getNotes({
        application_ids: [activePiPDeal.value.id],
      });
      return;
    }
    shouldUpdatePiPDeal.value = true;
  }
);

const communicationLogCallDialogRef = ref<InstanceType<
  typeof CommunicationLogCallDialog
> | null>(null);

const communicationLogNoteRef = ref<InstanceType<typeof HTMLDivElement> | null>(
  null
);

const PipParams = computed(() => ({
  [PipType.Call]: {
    htmlPiPElement: communicationLogCallDialogRef.value?.resizableElement,
    width: callOptions.value.width,
    height: callOptions.value.height,
    actionOnClose: closePiPModal,
  },
  [PipType.DealActivity]: {
    htmlPiPElement: communicationLogNoteRef.value,
    width: noteOptions.value.width,
    height: noteOptions.value.height,
    actionOnClose: closePiPModal,
  },
}));

const closePiPModal = () => {
  window.documentPictureInPicture?.window.close();
  if (type === PipType.DealActivity) {
    noteOptions.value = {
      ...noteOptions.value,
      state: closeDealActivityState.value,
    };
    unsetActivePiP();
  } else {
    callDialog.value = null;
  }
  emit("pip:hide");
};

window.documentPictureInPicture?.addEventListener("enter", () => {
  if (type === PipType.DealActivity) {
    isDealActivityPipModeActivated.value = true;
  }
});

onMounted(async () => {
  // Initially, type "Application Note" is selected
  activeNoteBusinessDealId.value = null;
  const canShowPip = await initPiPView(PipParams.value[type]);

  if (canShowPip) {
    return;
  }

  closeDealActivityState.value = DealActivityState.Draggable;
  closePiPModal();
  showMessage(t("COMMON.PIP.ERROR"), "warning");
});

watch(activePiPDeal, async (deal) => {
  if (deal?.id) {
    activePiPWorkflow.value = await apiService.getWorkflowSnapshot(deal.id);
    activePiPFlows.value = activePiPWorkflow.value?.content?.flow || [];
    activePiPStages.value = activePiPWorkflow.value?.content?.stages || [];
  }
});

watch(activeDeal, (updatedDeal) => {
  if (shouldUpdatePiPDeal.value && updatedDeal?.id) {
    activePiPDeal.value = updatedDeal;
    shouldUpdatePiPDeal.value = false;
  }
});
</script>
